import { IMActivity } from '../../../types'
import { parseHowManySeeHowSeeThem } from './howManySeeHowSeeThem'
import { parseGoTogether } from './goTogether'
import { parseNoticeWonder } from './noticeWonder'
import { parseEstimationExploration } from './estimationExploration'
import { parseMultiPart, parseMultiQuestion } from './multiPart'
import { parseLastResort } from './lastResort'
import { ISimplifiedNode } from '../../../../../components/ingestion/IngestedTree'
import { parseHtmlString } from 'ingestion/nodeCreationHelpers'
import { LeafTypes, NodeType } from 'shared/types/ingestion'
import { parseTrickyMultipart } from './trickyMultiQuestion'
import { parseSelectAll } from './selectAllStatements'

type questionParser = (
  data: { title?: string; statement?: string; studentResponse?: string },
  activity?: IMActivity,
) => ISimplifiedNode[] | undefined

const multiQParserList: questionParser[] = [
  parseTrickyMultipart,
  parseGoTogether,
  parseEstimationExploration,
  parseSelectAll,
  parseMultiQuestion,
  parseHowManySeeHowSeeThem,
  parseNoticeWonder,
  parseLastResort,
]

const multiPartParserList: questionParser[] = [
  parseTrickyMultipart,
  parseHowManySeeHowSeeThem,
  parseGoTogether,
  parseEstimationExploration,
  parseSelectAll,
  parseMultiPart,
  parseNoticeWonder,
  parseLastResort,
]

export function getQuestions(data: IMActivity) {
  if (data.digital_component?.statement?.trim()) {
    return prepareQuestionsMultiQ(
      {
        extension: data.print_component?.extension,
        extension_student_response:
          data.print_component?.extension_student_response,
        ...data.digital_component,
      },
      data,
    )
  }
  if (data.print_component) {
    return prepareQuestionsMultiQ(data.print_component, data)
  }
  return []
}

export function prepareQuestionsMultiQ(
  data: {
    statement?: string
    student_response?: string
    extension?: string
    extension_student_response?: string
  },
  activity?: IMActivity,
) {
  return prepareQuestions(data, multiQParserList, activity)
}

export function prepareQuestionsMultiPart(
  data: {
    statement?: string
    student_response?: string
    extension?: string
    extension_student_response?: string
  },
  activity?: IMActivity,
) {
  return prepareQuestions(data, multiPartParserList, activity)
}

function prepareQuestions(
  {
    statement,
    student_response,
    extension,
    extension_student_response,
  }: {
    statement?: string
    student_response?: string
    extension?: string
    extension_student_response?: string
  },
  parsers: questionParser[],
  activity?: IMActivity,
) {
  return [
    [statement, student_response, undefined],
    [extension, extension_student_response, 'Are You Ready For More?'],
  ].reduce<ISimplifiedNode[]>((qNodes, [statement, studentResponse, title]) => {
    const nodes =
      parsers.reduce<ISimplifiedNode[] | undefined>((acc, func) => {
        if (acc) return acc
        const nodes = func({ statement, studentResponse, title }, activity)
        const geoCode = statement && getGeogebraCode(statement)
        if (geoCode) {
          nodes?.forEach(({ type, data }) => {
            if (type === NodeType.Question) {
              data[LeafTypes.AppletGeogebra] = [geoCode]
            }
          })
        }
        return nodes
      }, undefined) ?? []
    return [...qNodes, ...nodes]
  }, [])
}

const geogebraAppletClass = 'geogebra-applet'
const geogebraUrlAttr = 'data-applet-url'
const geogebraUrl = 'www.geogebra.org/m/'

function getGeogebraCode(statement: string) {
  const withGeog =
    statement.includes(geogebraAppletClass) || statement.includes(geogebraUrl)
  if (!withGeog) return

  const asHtml = parseHtmlString(statement)

  const elm = asHtml.getElementsByClassName(geogebraAppletClass).item(0)
  if (elm) {
    return elm.getAttribute(geogebraUrlAttr)?.split('/').pop()
  }

  const anchor = Array.from(asHtml.getElementsByTagName('a')).find((a) =>
    a.href.includes(geogebraUrl),
  )
  if (!anchor) {
    throw new Error('Not finding geogebra code')
  }
  return anchor.href.split('/').pop()
}
